import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, Check, Loader2 } from "lucide-react";
import { useLang } from "@/lib/lang";
import { CONTACT, LANGS } from "@/content";
import { Eyebrow, MaskLines, Reveal } from "./motion";

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : null;
const EMPTY = { first_name: "", last_name: "", email: "", phone: "", message: "" };

const Field = ({ label, name, value, onChange, type = "text", textarea = false }) => (
  <label className="field block">
    <span className="block text-[12px] uppercase tracking-[0.22em] text-[var(--ink-soft)]">{label}</span>
    {textarea ? (
      <textarea name={name} value={value} onChange={onChange} rows={3} data-testid={`contact-${name}-input`} className="field-input resize-none" />
    ) : (
      <input name={name} type={type} value={value} onChange={onChange} data-testid={`contact-${name}-input`} className="field-input" dir={type === "email" || type === "tel" ? "ltr" : undefined} />
    )}
  </label>
);

export const Contact = () => {
  const { t, lang } = useLang();
  const f = t.contact.form;
  const [form, setForm] = useState(EMPTY);
  const [pref, setPref] = useState(lang);
  const [state, setState] = useState("idle");

  const onChange = (e) => setForm((s) => ({ ...s, [e.target.name]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.first_name.trim() || !form.last_name.trim() || !/^\S+@\S+\.\S+$/.test(form.email)) {
      toast.error(t.contact.required);
      return;
    }
    if (!API) { toast.error(t.contact.error); return; }
    setState("sending");
    try {
      await axios.post(`${API}/viewings`, { ...form, language: pref });
      setState("done");
      setForm(EMPTY);
      toast.success(t.contact.successTitle, { description: t.contact.success });
    } catch {
      setState("idle");
      toast.error(t.contact.error);
    }
  };

  return (
    <section id="contact" className="relative bg-[#F2F9F9] py-24 md:py-36" data-testid="contact-section">
      <div className="mx-auto max-w-[1440px] px-5 md:px-10 grid gap-16 lg:grid-cols-12">
        <div className="lg:col-span-5">
          <Eyebrow>{t.contact.eyebrow}</Eyebrow>
          <h2 className="mt-6 font-display text-[clamp(2.8rem,5.4vw,5.2rem)] leading-[0.95] text-[var(--ink)]">
            <MaskLines key={t.contact.title} lines={[t.contact.title]} />
          </h2>
          <Reveal><p className="mt-7 max-w-md text-[15px] leading-relaxed text-[var(--ink-soft)]">{t.contact.body}</p></Reveal>
          <Reveal className="mt-12 space-y-6">
            {[
              { l: t.contact.emailL, v: CONTACT.email, href: `mailto:${CONTACT.email}`, id: "email" },
              { l: t.contact.phoneL, v: CONTACT.phone, href: `tel:${CONTACT.tel}`, id: "phone" },
              { l: t.contact.officeL, v: t.contact.office, href: null, id: "office" },
            ].map((r) => (
              <div key={r.id} className="border-t border-[var(--line-strong)] pt-4" data-testid={`contact-info-${r.id}`}>
                <p className="text-[12px] uppercase tracking-[0.22em] text-[var(--brass-ink)]">{r.l}</p>
                {r.href ? <a href={r.href} className="mt-1 inline-block font-display text-2xl text-[var(--ink)] link-line" dir="ltr">{r.v}</a> : <p className="mt-1 font-display text-2xl text-[var(--ink)] rtl:text-right" dir="ltr">{r.v}</p>}
              </div>
            ))}
          </Reveal>
        </div>

        <Reveal className="lg:col-span-6 lg:col-start-7">
          <form onSubmit={submit} noValidate className="rounded-[32px] bg-white p-6 md:p-12 shadow-[0_40px_80px_-50px_rgba(14,95,115,0.45)]" data-testid="contact-form">
            <div className="grid gap-8 sm:grid-cols-2">
              <Field label={f.firstName} name="first_name" value={form.first_name} onChange={onChange} />
              <Field label={f.lastName} name="last_name" value={form.last_name} onChange={onChange} />
              <Field label={f.email} name="email" type="email" value={form.email} onChange={onChange} />
              <Field label={f.phone} name="phone" type="tel" value={form.phone} onChange={onChange} />
            </div>
            <div className="mt-8">
              <span className="block text-[12px] uppercase tracking-[0.22em] text-[var(--ink-soft)]">{f.prefLang}</span>
              <div className="mt-3 flex flex-wrap gap-2">
                {LANGS.map((l) => (
                  <button key={l.code} type="button" onClick={() => setPref(l.code)} data-testid={`contact-pref-${l.code}`}
                    className={`rounded-full border px-4 py-2 text-sm transition-colors duration-300 ${pref === l.code ? "border-[var(--brass-deep)] bg-[var(--brass-deep)] text-white" : "border-[var(--line-strong)] text-[var(--ink-soft)] hover:border-[var(--brass)]"}`}>
                    {l.name}
                  </button>
                ))}
              </div>
            </div>
            <div className="mt-8"><Field label={f.message} name="message" value={form.message} onChange={onChange} textarea /></div>
            <button type="submit" disabled={state === "sending"} className="btn-ink mt-10 w-full sm:w-auto justify-center group" data-testid="contact-submit-btn">
              {state === "sending" ? <><Loader2 className="h-4 w-4 animate-spin" />{f.sending}</> : state === "done" ? <><Check className="h-4 w-4" />{t.contact.successTitle}</> : <>{f.submit}<ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1 rtl:rotate-180 rtl:group-hover:-translate-x-1" /></>}
            </button>
            {state === "done" && <p className="mt-4 text-sm text-[var(--sea)]" data-testid="contact-success-msg">{t.contact.success}</p>}
          </form>
        </Reveal>
      </div>
    </section>
  );
};
